import React, { useEffect, useRef, useState } from "react";
import { SystemIcon, Typography } from "@volvo/vcdk-react";
import styles from "./FileUpload.module.css";
import "@volvo/vcdk/themes/all-semantic.css";
import { UploadFileConfiguration } from "../../../constants/const";
import log from "../../../logging/logger";

interface IFileUploadProps {
    onFileSelected?: (file: File) => void;
    clearOnSelect?: boolean;
    onFileClear?: () => void;
    externalFileName?: string;
    externalFileSize?: number;
    label?: string;
    isUploading?: boolean;
    disabled?: boolean;
}

const FileUpload = React.forwardRef<HTMLDivElement, IFileUploadProps>(
    (props, ref) => {
        const fileInputRef = useRef<HTMLInputElement>(null);

        const [selectedFile, setSelectedFile] = useState<File | null>(null);
        const [, setPreviewUrl] = useState<string | null>(null);
        const [error, setError] = useState<string>("");
        const [isDragOver, setIsDragOver] = useState(false);

        const MAX_SIZE = UploadFileConfiguration.MaxFileSize;

        useEffect(() => {
            if (!props.externalFileName && selectedFile) {
                setSelectedFile(null);
                setPreviewUrl(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        }, [props.externalFileName, selectedFile]);

        const loadPreview = (file: File): void => {
            const allowedTypes = UploadFileConfiguration.AllowedTypes;
            if (!allowedTypes.includes(file.type)) {
                setError(UploadFileConfiguration.IncorrectFileTypeMessage);
                setSelectedFile(null);
                setPreviewUrl(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }
            if (file.size > MAX_SIZE) {
                setError(UploadFileConfiguration.IncorrectFileSizeMessage);
                setSelectedFile(null);
                setPreviewUrl(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }
            setError("");
            setSelectedFile(file);
            const reader = new FileReader();
            if (!props.clearOnSelect) {
                reader.onloadend = () => {
                    setPreviewUrl(reader.result as string);
                };
            }
            reader.readAsDataURL(file);
            if (props.onFileSelected) {
                props.onFileSelected(file);
                log.info("File selected:", file);
            }
        };

        const handleClearFile = () => {
            setSelectedFile(null);
            setPreviewUrl(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            if (props.onFileClear) {
                props.onFileClear();
            }
        };

        const renderFileInfo = (file: File | null) => {
            if (!file) {
                return null;
            }
            return (
                <>
                    <div className={styles.AttachmentInfo}>
                        <SystemIcon
                            icon={
                                props.isUploading
                                    ? "refresh"
                                    : "checkmark-circle"
                            }
                            size={20}
                            className={props.isUploading ? styles.spinning : ""}
                        />
                        {file.name}
                        <span
                            style={{ cursor: "pointer" }}
                            title="Remove file"
                            onClick={handleClearFile}
                        >
                            <SystemIcon icon="trash" size={20} />
                        </span>
                    </div>
                </>
            );
        };

        const renderExternalFileInfo = () => {
            if (!props.externalFileName) {
                return null;
            }
            return (
                <>
                    <div className={styles.AttachmentInfo}>
                        <SystemIcon
                            icon={
                                props.isUploading ? "clock" : "checkmark-circle"
                            }
                            size={20}
                            className={props.isUploading ? styles.spinning : ""}
                        />
                        {props.externalFileName}
                        <span
                            style={{ cursor: "pointer" }}
                            title="Remove file"
                            onClick={handleClearFile}
                        >
                            <SystemIcon icon="trash" size={20} />
                        </span>
                    </div>
                </>
            );
        };

        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (props.disabled) {
                return;
            }
            const file = e.target.files?.[0];
            if (file) {
                loadPreview(file);
            }
        };

        const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (props.disabled) {
                return;
            }
            const file = e.dataTransfer.files?.[0];
            if (file) {
                loadPreview(file);
            }
        };

        const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (!props.disabled) {
                setIsDragOver(true);
            }
        };

        const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
        };

        return (
            <div className={styles.root} ref={ref}>
                <div className={styles.Label}>
                    <Typography variant="caption2">{props.label}</Typography>
                </div>
                <div
                    className={`${styles.uploadZone}${props.disabled ? ` ${styles.disabled}` : ""}${isDragOver ? ` ${styles.dragOver}` : ""}`}
                    onClick={() => {
                        if (!props.disabled) {
                            fileInputRef.current?.click();
                        }
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    style={{
                        pointerEvents: props.isUploading ? "none" : "auto",
                        opacity: props.isUploading ? 0.5 : 1,
                    }}
                >
                    <div className={styles.overlayContent}>
                        <div className={styles.iconContainer}>
                            <SystemIcon
                                icon={"upload-cloud"}
                                className={styles.icon}
                                size={36}
                            />
                            <Typography variant="caption2">
                                Drag and drop files here
                                <br />
                                or click to browse
                                <br />
                                {
                                    UploadFileConfiguration.UploadControlSizeAndTypeLabel
                                }
                            </Typography>
                        </div>
                    </div>
                    <input
                        type="file"
                        accept={UploadFileConfiguration.InputAllowedTypes}
                        className={styles.hiddenInput}
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                </div>
                <div className={styles.AttachmentInfoWrapper}>
                    {selectedFile
                        ? renderFileInfo(selectedFile)
                        : renderExternalFileInfo()}
                </div>
                <div className={styles.ErrorAttachmentWrapper}>
                    {error && (
                        <div>
                            <Typography
                                variant="caption2"
                                className={styles.ErrorMessage}
                            >
                                {error}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);
FileUpload.displayName = "FileUpload";

export default FileUpload;
